import { calcApplicableBonus } from '@/hooks/useWallet';
import type { GiftCode } from '@/lib/supabase';
import type { CartItem, Address } from '../types';
import { getProductByIdFromCache } from '../utils/productUtils';

interface CartSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  cart: CartItem[];
  cartTotal: number;
  cartCount: number;
  savedAddress: Address | null;
  walletBalance: number;
  applyBonusCart: boolean;
  setApplyBonusCart: (v: boolean) => void;
  giftCodeCart: string;
  setGiftCodeCart: (v: string) => void;
  giftCodeCartData: GiftCode | null;
  setGiftCodeCartData: (v: GiftCode | null) => void;
  giftCodeCartDiscount: number;
  setGiftCodeCartDiscount: (v: number) => void;
  giftCodeCartMsg: string;
  setGiftCodeCartMsg: (v: string) => void;
  giftCodeCartLoading: boolean;
  onApplyGiftCode: () => void;
  onRemoveFromCart: (id: number, size?: string) => void;
  onUpdateQuantity: (id: number, size: string | undefined, delta: number) => void;
  onCartOrder: () => void;
  onOpenAddressModal: () => void;
}

export default function CartSidebar({
  isOpen, onClose, cart, cartTotal, cartCount,
  savedAddress, walletBalance, applyBonusCart, setApplyBonusCart,
  giftCodeCart, setGiftCodeCart, giftCodeCartData, setGiftCodeCartData,
  giftCodeCartDiscount, setGiftCodeCartDiscount, giftCodeCartMsg, setGiftCodeCartMsg,
  giftCodeCartLoading, onApplyGiftCode,
  onRemoveFromCart, onUpdateQuantity, onCartOrder, onOpenAddressModal,
}: CartSidebarProps) {
  if (!isOpen) return null;

  const walletDiscount = applyBonusCart ? calcApplicableBonus(walletBalance, cartTotal) : 0;
  const finalPayable = cartTotal - walletDiscount - giftCodeCartDiscount;
  const productDiscount = cart.reduce((s, i) => s + (i.originalPrice - i.price) * i.quantity, 0);
  const totalSaved = productDiscount + walletDiscount + giftCodeCartDiscount;
  const cartScratchPct = finalPayable < 500 ? 7 : 10;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-md bg-white dark:bg-slate-900 h-full flex flex-col animate-slide-in-right shadow-2xl">
        <div className="flex-1 overflow-y-auto p-6 pb-2">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Shopping Cart ({cartCount})</h3>
            <button onClick={onClose} className="w-10 h-10 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-gray-600 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors cursor-pointer">
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {cart.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
                <i className="ri-shopping-bag-line text-3xl text-gray-400 dark:text-gray-500"></i>
              </div>
              <p className="text-gray-500 dark:text-gray-300">Your cart is empty</p>
              <button onClick={onClose} className="mt-4 px-6 py-2 rounded-full gradient-primary text-white text-sm font-medium cursor-pointer">
                Continue Shopping
              </button>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div className="space-y-4 mb-6">
                {cart.map(item => {
                  const liveProduct = getProductByIdFromCache(item.id);
                  const liveColorImage = liveProduct?.colors?.find(c => c.name === item.selectedColor?.name)?.image;
                  const resolvedImage = liveColorImage ?? liveProduct?.image ?? item.image ?? '';
                  return (
                    <div key={`${item.id}-${item.size}`} className="flex gap-4 p-4 rounded-xl bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700">
                      {resolvedImage ? (
                        <img src={resolvedImage} alt={item.name}
                          className="w-20 h-24 object-cover object-top rounded-lg flex-shrink-0 bg-gray-100 dark:bg-slate-700"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                      ) : (
                        <div className="w-20 h-24 rounded-lg flex-shrink-0 bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
                          <i className="ri-t-shirt-line text-2xl text-gray-300 dark:text-gray-500"></i>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-gray-900 dark:text-white font-medium text-sm leading-tight">{item.name}</h4>
                        <p className="text-gray-500 dark:text-gray-400 text-xs">
                          {item.category} · Size: {item.size}
                          {item.selectedColor && <span className="ml-1">· Color: <span style={{ color: item.selectedColor.hex }}>{item.selectedColor.name}</span></span>}
                        </p>
                        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                          <span className="text-[var(--theme-primary)] font-bold text-base">₹{item.price}</span>
                          <span className="text-gray-400 dark:text-gray-500 text-xs line-through">₹{item.originalPrice}</span>
                          <span className="text-xs text-green-600 font-semibold">{Math.round((1 - item.price / item.originalPrice) * 100)}% off</span>
                        </div>
                        <div className="mt-1 inline-flex items-center gap-1 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-full px-2 py-0.5">
                          <i className="ri-price-tag-3-fill text-green-600 text-[10px]"></i>
                          <span className="text-green-600 dark:text-green-400 text-[10px] font-semibold">Save ₹{(item.originalPrice - item.price) * item.quantity}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <button onClick={() => onUpdateQuantity(item.id, item.size, -1)}
                            className="w-8 h-8 rounded-full bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600 cursor-pointer">
                            <i className="ri-subtract-line text-xs"></i>
                          </button>
                          <span className="text-gray-900 dark:text-white font-medium">{item.quantity}</span>
                          <button onClick={() => onUpdateQuantity(item.id, item.size, 1)}
                            className="w-8 h-8 rounded-full bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600 cursor-pointer">
                            <i className="ri-add-line text-xs"></i>
                          </button>
                          <button onClick={() => onRemoveFromCart(item.id, item.size)}
                            className="ml-auto text-gray-400 dark:text-gray-500 hover:text-red-500 transition-colors cursor-pointer">
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Delivery Address */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1">
                    <i className="ri-map-pin-2-fill text-red-500"></i>Delivery Address
                  </span>
                  <button onClick={onOpenAddressModal} className="text-xs font-semibold hover:underline cursor-pointer" style={{ color: 'var(--theme-primary)' }}>
                    {savedAddress ? 'Change' : 'Add Address'}
                  </button>
                </div>
                {savedAddress ? (
                  <div className="p-3 rounded-xl bg-green-50 border border-green-200 dark:border-green-800">
                    {savedAddress.lat && savedAddress.lng && (
                      <div className="w-full rounded-lg overflow-hidden mb-2" style={{ height: '110px' }}>
                        <iframe title="Cart Delivery Location" width="100%" height="110" style={{ border: 0 }} loading="lazy"
                          src={`https://maps.google.com/maps?q=${savedAddress.lat},${savedAddress.lng}&z=15&output=embed`}></iframe>
                      </div>
                    )}
                    <div className="flex items-start gap-2">
                      <div className="w-7 h-7 rounded-lg bg-green-500/15 flex items-center justify-center shrink-0">
                        <i className="ri-home-5-fill text-green-600 text-sm"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 dark:text-white text-sm font-semibold">{savedAddress.fullName}</p>
                        <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed">{savedAddress.address}</p>
                        <p className="text-gray-600 dark:text-gray-400 text-xs">{savedAddress.city}{savedAddress.pincode ? ` - ${savedAddress.pincode}` : ''}</p>
                        <p className="text-gray-500 text-xs mt-0.5"><i className="ri-phone-line mr-1"></i>{savedAddress.phone}</p>
                      </div>
                      {savedAddress.lat && <span className="text-xs bg-green-500/15 text-green-600 px-2 py-0.5 rounded-full font-medium shrink-0 whitespace-nowrap"><i className="ri-crosshair-2-line mr-0.5"></i>GPS</span>}
                    </div>
                  </div>
                ) : (
                  <button onClick={onOpenAddressModal}
                    className="w-full p-3 rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-600 bg-white/50 dark:bg-slate-800/50 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 hover:border-[var(--theme-primary)] hover:text-[var(--theme-primary)] transition-all cursor-pointer">
                    <i className="ri-map-pin-add-line text-lg"></i>
                    <span className="text-sm font-medium">Add Delivery Address</span>
                  </button>
                )}
              </div>

              {/* Payment Method */}
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-1">
                  <i className="ri-secure-payment-line text-gray-600"></i>Payment Method
                </p>
                <div className="p-3 rounded-xl bg-amber-50 border-2 border-amber-400 dark:border-amber-600 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-400/20 flex items-center justify-center shrink-0">
                    <i className="ri-money-rupee-circle-fill text-amber-600 text-lg"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 dark:text-white text-sm font-bold">Cash on Delivery</p>
                    <p className="text-gray-500 text-xs dark:text-gray-400">Pay when your order arrives</p>
                  </div>
                  <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                </div>
              </div>

              {/* Gift Code */}
              <div className="mb-4 bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-4">
                <p className="text-gray-800 dark:text-gray-200 text-sm font-bold mb-2 flex items-center gap-2">
                  <i className="ri-coupon-3-line text-pink-500"></i>Gift Code Apply Karo
                </p>
                {giftCodeCartData ? (
                  <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-xl px-3 py-2.5">
                    <i className="ri-check-line text-green-600"></i>
                    <span className="text-green-700 dark:text-green-400 font-bold text-sm flex-1">{giftCodeCart} — ₹{giftCodeCartDiscount} off!</span>
                    <button onClick={() => { setGiftCodeCartData(null); setGiftCodeCartDiscount(0); setGiftCodeCart(''); setGiftCodeCartMsg(''); }} className="text-gray-400 hover:text-red-500 cursor-pointer"><i className="ri-close-line"></i></button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input type="text" value={giftCodeCart} onChange={e => setGiftCodeCart(e.target.value.toUpperCase())}
                      onKeyDown={e => e.key === 'Enter' && onApplyGiftCode()}
                      placeholder="Enter gift code..."
                      className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm font-mono uppercase focus:border-pink-400 outline-none" />
                    <button onClick={onApplyGiftCode} disabled={giftCodeCartLoading || !giftCodeCart.trim()}
                      className="px-4 py-2.5 rounded-xl bg-pink-500 text-white font-bold text-sm cursor-pointer hover:bg-pink-600 transition-all disabled:opacity-50 whitespace-nowrap">
                      {giftCodeCartLoading ? <i className="ri-loader-4-line animate-spin"></i> : 'Apply'}
                    </button>
                  </div>
                )}
                {giftCodeCartMsg && !giftCodeCartData && <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="ri-error-warning-line"></i>{giftCodeCartMsg}</p>}
              </div>

              {/* Wallet */}
              <div className={`mb-4 rounded-2xl p-4 border ${walletBalance > 0 ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' : 'bg-gray-50 dark:bg-slate-800 border-gray-100 dark:border-slate-700'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <i className="ri-wallet-3-fill text-amber-500 text-lg"></i>
                    <div>
                      <p className="text-gray-800 dark:text-gray-200 text-sm font-semibold">Wallet Balance</p>
                      <p className={`text-xs ${walletBalance > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'}`}>
                        {walletBalance > 0 ? `₹${walletBalance} available (deposit + cashback)` : 'No balance — add from Profile → Wallet'}
                      </p>
                    </div>
                  </div>
                  {walletBalance > 0 && (
                    <button onClick={() => setApplyBonusCart(!applyBonusCart)}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer transition-all whitespace-nowrap ${applyBonusCart ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300'}`}>
                      {applyBonusCart ? '✓ Applied' : 'Apply'}
                    </button>
                  )}
                </div>
                {applyBonusCart && <p className="mt-1.5 text-green-600 dark:text-green-400 text-xs flex items-center gap-1"><i className="ri-checkbox-circle-fill"></i>₹{calcApplicableBonus(walletBalance, cartTotal)} wallet balance applied!</p>}
              </div>

              <div className="mb-3 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
                <i className="ri-coupon-4-fill text-amber-500 text-sm"></i>
                <p className="text-amber-700 text-xs">
                  <span className="font-bold">Mystery scratch card</span> milega! Scratch karke <span className="font-bold">{cartScratchPct}% bonus</span> reveal karo!
                </p>
              </div>

              {/* Order Summary */}
              <div className="border-t border-gray-100 dark:border-slate-700 pt-4 mb-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">MRP ({cart.reduce((s, i) => s + i.quantity, 0)} items)</span>
                  <span className="text-gray-400 dark:text-gray-500 text-sm line-through">₹{cart.reduce((s, i) => s + i.originalPrice * i.quantity, 0)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">Product Discount</span>
                  <span className="text-green-600 dark:text-green-400 text-sm font-semibold">- ₹{productDiscount}</span>
                </div>
                {giftCodeCartDiscount > 0 && (
                  <div className="flex items-center justify-between bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded-xl px-3 py-2">
                    <span className="text-pink-700 dark:text-pink-400 text-sm flex items-center gap-1"><i className="ri-coupon-3-fill text-pink-500"></i>Gift Code ({giftCodeCart})</span>
                    <span className="text-pink-700 dark:text-pink-400 text-sm font-bold">- ₹{giftCodeCartDiscount}</span>
                  </div>
                )}
                {walletDiscount > 0 && (
                  <div className="flex items-center justify-between bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2">
                    <span className="text-amber-700 dark:text-amber-400 text-sm flex items-center gap-1"><i className="ri-wallet-3-fill text-amber-500"></i>Wallet Balance Applied</span>
                    <span className="text-amber-700 dark:text-amber-400 text-sm font-bold">- ₹{walletDiscount}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">Delivery Charges</span>
                  <span className="text-green-600 dark:text-green-400 text-sm font-semibold">FREE</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-slate-700">
                  <span className="text-gray-900 dark:text-white font-bold">Total to Pay</span>
                  <div className="text-right">
                    {walletDiscount > 0 && <p className="text-gray-400 dark:text-gray-500 text-xs line-through text-right">₹{cartTotal}</p>}
                    <span className="text-gray-900 dark:text-white font-bold text-xl">₹{finalPayable}</span>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl py-2.5 px-4">
                  <i className="ri-gift-line text-green-600 dark:text-green-400 text-base"></i>
                  <span className="text-green-700 dark:text-green-400 text-sm font-bold">You saved ₹{totalSaved} on this order!</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Sticky Bottom */}
        {cart.length > 0 && (
          <div className="shrink-0 px-6 py-4 border-t border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900">
            <button onClick={onCartOrder}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold text-base hover:shadow-lg hover:shadow-green-500/30 transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap">
              <i className="ri-truck-line text-lg"></i>
              Order Now - COD · ₹{finalPayable}
            </button>
            <p className="text-center text-gray-400 text-xs mt-2">Cash on Delivery · Pay ₹{finalPayable} when order arrives</p>
            <button onClick={onClose}
              className="w-full mt-2 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 text-sm hover:bg-gray-50 dark:hover:bg-slate-800 transition-all cursor-pointer">
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
